// Store Zustand pour la gestion du dashboard avec cache

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PeriodeDashboard, DashboardSummary } from '../types/dashboard';

interface DashboardCache {
  data: DashboardSummary | null;
  timestamp: number | null;
  periode: PeriodeDashboard;
}

interface DashboardStore {
  periode: PeriodeDashboard;
  cache: Record<PeriodeDashboard, DashboardCache>;
  userId: string | null; // ID de l'utilisateur propriétaire du cache
  setPeriode: (periode: PeriodeDashboard) => void;
  setData: (periode: PeriodeDashboard, data: DashboardSummary, userId: string) => void;
  getData: (periode: PeriodeDashboard, userId: string) => DashboardSummary | null;
  isCacheValid: (periode: PeriodeDashboard, userId: string, maxAgeMinutes?: number) => boolean;
  invalidateCache: () => void;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes par défaut

export const useDashboardStore = create<DashboardStore>()(
  persist(
    (set, get) => ({
      periode: PeriodeDashboard.MOIS, // Période par défaut
      userId: null,
      cache: {
        [PeriodeDashboard.MOIS]: { data: null, timestamp: null, periode: PeriodeDashboard.MOIS },
        [PeriodeDashboard.TRIMESTRE]: { data: null, timestamp: null, periode: PeriodeDashboard.TRIMESTRE },
        [PeriodeDashboard.ANNEE]: { data: null, timestamp: null, periode: PeriodeDashboard.ANNEE },
      },

      setPeriode: (periode) => set({ periode }),

      setData: (periode, data, userId) => {
        // Vérifier si le cache appartient à cet utilisateur
        const currentUserId = get().userId;

        // Si l'utilisateur a changé, invalider le cache
        if (currentUserId && currentUserId !== userId) {
          console.log('🔄 Changement d\'utilisateur détecté, invalidation du cache');
          set({
            userId,
            cache: {
              [PeriodeDashboard.MOIS]: { data: null, timestamp: null, periode: PeriodeDashboard.MOIS },
              [PeriodeDashboard.TRIMESTRE]: { data: null, timestamp: null, periode: PeriodeDashboard.TRIMESTRE },
              [PeriodeDashboard.ANNEE]: { data: null, timestamp: null, periode: PeriodeDashboard.ANNEE },
            },
          });
        }

        set((state) => ({
          userId,
          cache: {
            ...state.cache,
            [periode]: {
              data,
              timestamp: Date.now(),
              periode,
            },
          },
        }));
      },

      getData: (periode, userId) => {
        const currentUserId = get().userId;

        // Si l'utilisateur ne correspond pas, ne pas utiliser le cache
        if (currentUserId !== userId) {
          console.log('⚠️ Cache invalide : utilisateur différent');
          return null;
        }

        const cache = get().cache[periode];
        return cache?.data || null;
      },

      isCacheValid: (periode, userId, maxAgeMinutes = 5) => {
        const currentUserId = get().userId;

        // Si l'utilisateur ne correspond pas, le cache n'est pas valide
        if (currentUserId !== userId) {
          return false;
        }

        const cache = get().cache[periode];
        if (!cache || !cache.data || !cache.timestamp) {
          return false;
        }
        const age = Date.now() - cache.timestamp;
        const maxAge = maxAgeMinutes * 60 * 1000;
        return age < maxAge;
      },

      invalidateCache: () => {
        set({
          userId: null,
          cache: {
            [PeriodeDashboard.MOIS]: { data: null, timestamp: null, periode: PeriodeDashboard.MOIS },
            [PeriodeDashboard.TRIMESTRE]: { data: null, timestamp: null, periode: PeriodeDashboard.TRIMESTRE },
            [PeriodeDashboard.ANNEE]: { data: null, timestamp: null, periode: PeriodeDashboard.ANNEE },
          },
        });
      },
    }),
    {
      name: 'dashboard-cache',
      partialize: (state) => ({ cache: state.cache, userId: state.userId }), // Persister le cache ET l'userId
    }
  )
);
